import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  SkeletonRows,
  Tabs,
  Textarea,
  useToast,
} from "../components/ui";
import { projectService, type Project } from "../services/project.service";
import {
  builderService,
  type ChatMessage,
  type RunChanges,
  type RunStep,
  type RunSummary,
  type WorkspaceFile,
  type VisualSelection,
} from "../services/builder.service";
import PreviewPanel from "../components/PreviewPanel";
import DatabasePanel from "../components/DatabasePanel";
import VersionsPanel from "../components/VersionsPanel";
import DeployPanel from "../components/DeployPanel";
import AdvancedModeToggle from "../components/AdvancedModeToggle";
import JourneySteps from "../components/JourneySteps";
import {
  plainStep,
  plainSummary,
  technicalSummary,
  useAdvancedMode,
} from "../lib/plain-language";

/**
 * Tela principal do Builder: /projects/:id/builder
 * Esquerda: chat com a IA. Centro: preview. Direita: painéis auxiliares.
 * Nenhum raciocínio interno da IA é exibido — apenas etapas e resultados.
 */
export default function BuilderPage() {
  const { id = "" } = useParams();
  const toast = useToast();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversation, setConversation] = useState<string>("");
  const [input, setInput] = useState("");
  const [steps, setSteps] = useState<RunStep[]>([]);
  const [running, setRunning] = useState(false);
  const [runUuid, setRunUuid] = useState<string | null>(null);
  const [lastPrompt, setLastPrompt] = useState("");
  const [changes, setChanges] = useState<RunChanges | null>(null);
  const [revertTarget, setRevertTarget] = useState<RunChanges | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [selection, setSelection] = useState<VisualSelection | null>(null);
  const [advanced] = useAdvancedMode();

  const closeStream = useRef<(() => void) | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const composer = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let active = true;

    Promise.all([projectService.get(id), builderService.conversation(id)])
      .then(([proj, conv]) => {
        if (!active) return;
        setProject(proj);
        setConversation(conv.uuid);
        setMessages(conv.messages);
      })
      .catch((error: Error) => toast.error(error.message))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
      closeStream.current?.();
    };
  }, [id, toast]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages, steps]);

  useEffect(() => {
    if (!running) composer.current?.querySelector("textarea")?.focus();
  }, [running, loading]);

  const send = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message || running || !conversation) return;

      setLastPrompt(message);
      setInput("");
      setSteps([]);
      setRunning(true);

      const selected = selection;
      setSelection(null);

      closeStream.current = builderService.stream(
        id,
        conversation,
        message,
        {
          onMessage: (item) => setMessages((current) => [...current, item]),
          onRun: (uuid) => setRunUuid(uuid),
          onStep: (step) =>
            setSteps((current) => {
              const next = current.filter((item) => item.sequence !== step.sequence);
              return [...next, step].sort((a, b) => a.sequence - b.sequence);
            }),
          onDone: (item) => {
            setMessages((current) => [...current, item]);
            setRunning(false);
            setRunUuid(null);
          },
          onError: (item) => {
            setMessages((current) => [...current, item]);
            setRunning(false);
            setRunUuid(null);
          },
        },
        selected,
      );
    },
    [conversation, id, running, selection],
  );

  const stop = useCallback(async () => {
    if (!runUuid) return;
    try {
      await builderService.cancel(id, runUuid);
      toast.notify("Parando...");
    } catch (error) {
      toast.error((error as Error).message);
    }
  }, [id, runUuid, toast]);

  const openChanges = useCallback(
    async (run: string) => {
      try {
        setChanges(await builderService.changes(id, run));
      } catch (error) {
        toast.error((error as Error).message);
      }
    },
    [id, toast],
  );

  // "Tentar corrigir novamente" após o pipeline reprovar todas as tentativas.
  const retryRun = useCallback(
    async (run: string) => {
      setRetrying(true);
      try {
        const result = await builderService.retry(id, run);
        setMessages((current) => [
          ...current,
          {
            uuid: `retry-${result.run ?? run}`,
            type: result.status === "failed" ? "error" : "assistant",
            content: result.content,
            metadata: { run: result.run, summary: result.summary },
          },
        ]);
        if (result.status === "failed") {
          toast.error("Ainda não ficou certo. Tente descrever o que está errado.");
        } else {
          toast.success("Corrigido e verificado.");
        }
      } catch (error) {
        toast.error((error as Error).message);
      } finally {
        setRetrying(false);
      }
    },
    [id, toast],
  );

  const revert = useCallback(async () => {
    if (!revertTarget?.version_uuid) return;
    try {
      const restored = await builderService.restore(id, revertTarget.version_uuid);
      toast.success(`Alteração desfeita (ponto #${restored.number}).`);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setRevertTarget(null);
      setChanges(null);
    }
  }, [id, revertTarget, toast]);

  if (loading) return <SkeletonRows rows={6} />;

  if (!project) {
    return (
      <EmptyState
        title="Projeto não encontrado"
        description="Ele pode ter sido excluído ou está fora do seu escopo."
        action={
          <Link to="/projects" className="text-sm text-brand hover:underline">
            Voltar para projetos
          </Link>
        }
      />
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-3 lg:flex-row">
      {/* ESQUERDA — chat */}
      <section className="flex min-h-[24rem] w-full flex-col overflow-hidden rounded-lg border border-line bg-surface lg:w-[26rem]">
        <header className="space-y-2 border-b border-line px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-sm font-semibold">{project.name}</h1>
              <p className="text-xs text-ink-muted">
                Escreva o que você quer, como se estivesse explicando para uma pessoa.
              </p>
            </div>
            <Badge tone={running ? "warn" : "ok"}>
              {running ? "criando seu sistema" : "pronto"}
            </Badge>
          </div>
          <JourneySteps current={running ? 3 : 5} />
        </header>

        <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.length === 0 && !running && (
            <div className="space-y-2 rounded-md border border-dashed border-line px-3 py-6 text-center text-xs text-ink-muted">
              <p className="text-ink">Conte o que o seu sistema precisa fazer.</p>
              <p>Exemplo: “Quero cadastrar clientes com nome, telefone e cidade.”</p>
              <p>Exemplo: “Mostre um painel com o total de clientes ativos.”</p>
            </div>
          )}

          {messages.map((message) => (
            <MessageBubble
              key={message.uuid}
              message={message}
              onChanges={openChanges}
              onRetry={retryRun}
              retrying={retrying}
            />
          ))}

          {running && <StepList steps={steps} />}
        </div>

        <footer className="space-y-2 border-t border-line px-4 py-3">
          <div ref={composer}>
            <Textarea
              rows={3}
              value={input}
              disabled={running}
              placeholder="Ex.: quero uma tela para cadastrar clientes"
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send(input);
                }
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => send(input)} disabled={running || input.trim() === ""}>
              Enviar
            </Button>
            {running && (
              <Button variant="secondary" onClick={stop}>
                Parar
              </Button>
            )}
            {!running && lastPrompt !== "" && (
              <Button variant="secondary" onClick={() => send(lastPrompt)}>
                Tentar novamente
              </Button>
            )}
          </div>
        </footer>
      </section>

      {/* CENTRO — preview */}
      <PreviewPanel
        projectId={id}
        onSelect={(item) => {
          setSelection(item);
          toast.notify("Item selecionado. Agora diga o que mudar nele.");
          composer.current?.querySelector("textarea")?.focus();
        }}
      />

      {/* DIREITA — painel auxiliar */}
      <section className="w-full overflow-y-auto rounded-lg border border-line bg-surface p-3 lg:w-[22rem]">
        <AuxiliaryPanel projectId={id} />
      </section>

      <Dialog open={changes !== null} onClose={() => setChanges(null)} title="O que mudou" size="lg">
        {changes && (
          <div className="space-y-3">
            <SummaryList summary={changes.summary} />
            {advanced && (
              <>
                <ChangedFiles projectId={id} diff={changes.diff} />
                <pre className="max-h-80 overflow-auto rounded-md border border-line bg-surface-raised p-3 font-mono text-[11px] leading-relaxed">
                  {changes.diff || "Sem diferenças registradas para esta execução."}
                </pre>
              </>
            )}
            {changes.version_uuid && (
              <Button variant="danger" onClick={() => setRevertTarget(changes)}>
                Desfazer esta alteração
              </Button>
            )}
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={revertTarget !== null}
        title="Desfazer esta alteração"
        description="O sistema volta ao estado anterior a esta alteração. Nada é perdido: o ponto atual continua guardado no histórico."
        confirmLabel="Desfazer"
        destructive
        onCancel={() => setRevertTarget(null)}
        onConfirm={revert}
      />
    </div>
  );
}

/** Arquivos tocados pela execução, com atalho para a comparação ANTES|DEPOIS. */
function ChangedFiles({ projectId, diff }: { projectId: string; diff: string }) {
  const paths = Array.from(
    new Set(
      (diff.match(/^\+\+\+ b\/(.+)$/gm) ?? []).map((line) => line.replace("+++ b/", "").trim()),
    ),
  );

  if (paths.length === 0) return null;

  return (
    <ul className="space-y-1">
      {paths.map((path) => (
        <li key={path}>
          <Link
            to={`/projects/${projectId}/editor?path=${encodeURIComponent(path)}`}
            className="font-mono text-[11px] text-brand hover:underline"
          >
            {path}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function MessageBubble({
  message,
  onChanges,
  onRetry,
  retrying,
}: {
  message: ChatMessage;
  onChanges: (run: string) => void;
  onRetry: (run: string) => void;
  retrying: boolean;
}) {
  if (message.type === "user") {
    return (
      <div className="ml-6 rounded-md bg-brand/15 px-3 py-2 text-sm whitespace-pre-wrap">
        {message.content}
      </div>
    );
  }

  const tone =
    message.type === "error" ? "border-danger/50 bg-danger/10" : "border-line bg-surface-raised";
  const run = message.metadata?.run ?? null;
  const failed = message.metadata?.summary?.build === "failed";

  return (
    <div className={`mr-6 space-y-2 rounded-md border px-3 py-2 text-sm ${tone}`}>
      <p className="whitespace-pre-wrap">{message.content}</p>
      {message.metadata?.summary && <SummaryList summary={message.metadata.summary} />}
      <div className="flex flex-wrap gap-2">
        {run && (
          <Button variant="secondary" size="sm" onClick={() => onChanges(run)}>
            Ver o que mudou
          </Button>
        )}
        {run && failed && (
          <Button variant="primary" size="sm" loading={retrying} onClick={() => onRetry(run)}>
            Tentar corrigir
          </Button>
        )}
      </div>
    </div>
  );
}

function SummaryList({ summary }: { summary: RunSummary }) {
  const [advanced] = useAdvancedMode();
  const items = advanced ? technicalSummary(summary) : plainSummary(summary);

  return (
    <ul className="space-y-1 text-xs text-ink-muted">
      {items.map((item) => (
        <li key={item}>• {item}</li>
      ))}
    </ul>
  );
}

const stepTone: Record<string, string> = {
  running: "text-brand",
  succeeded: "text-ink",
  skipped: "text-ink-muted",
  failed: "text-danger",
};

function StepList({ steps }: { steps: RunStep[] }) {
  const [advanced] = useAdvancedMode();

  const visible = useMemo(() => {
    const seen = new Set<string>();
    return steps
      .map((step) => ({ ...step, text: plainStep(step, advanced) }))
      .filter((step) => {
        // Sem detalhes técnicos, etapas seguidas com o mesmo texto viram uma só.
        const key = `${step.text}-${step.status}`;
        if (seen.has(key) && step.status !== "running") return false;
        seen.add(key);
        return true;
      });
  }, [steps, advanced]);

  return (
    <ul className="space-y-1 rounded-md border border-line bg-surface-raised px-3 py-2 text-xs">
      {visible.length === 0 && <li className="text-ink-muted">Entendendo o seu pedido...</li>}
      {visible.map((step) => (
        <li key={`${step.sequence}-${step.status}`} className={stepTone[step.status] ?? "text-ink"}>
          {step.status === "succeeded" ? "✓" : step.status === "failed" ? "✕" : "•"} {step.text}
        </li>
      ))}
    </ul>
  );
}

function AuxiliaryPanel({ projectId }: { projectId: string }) {
  const [files, setFiles] = useState<WorkspaceFile[]>([]);
  const [advanced] = useAdvancedMode();

  useEffect(() => {
    if (!advanced) return;
    builderService
      .files(projectId)
      .then(setFiles)
      .catch(() => setFiles([]));
  }, [projectId, advanced]);

  // Sempre visível: o que o cliente comum precisa (histórico e publicação).
  const simpleTabs = [
    {
      id: "versions",
      label: "Histórico",
      content: (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          <p className="text-xs text-ink-muted">
            Cada alteração vira um ponto de retorno. Se algo sair errado, volte para um ponto
            anterior.
          </p>
          <Link
            to={`/projects/${projectId}/versions`}
            className="inline-block text-xs text-brand hover:underline"
          >
            Ver histórico completo
          </Link>
          <VersionsPanel projectId={projectId} />
        </div>
      ),
    },
    {
      id: "deploy",
      label: "Publicar",
      content: (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          <p className="text-xs text-ink-muted">
            Quando o sistema estiver do seu jeito, coloque-o no ar para as outras pessoas usarem.
          </p>
          <DeployPanel projectId={projectId} />
        </div>
      ),
    },
  ];

  // Área técnica avançada: só aparece com "Detalhes técnicos" ligado.
  const advancedTabs = [
    {
      id: "files",
      label: "Arquivos",
      content: (
        <div className="space-y-2">
          <Link
            to={`/projects/${projectId}/editor`}
            className="inline-block text-xs text-brand hover:underline"
          >
            Abrir editor de código
          </Link>
          <ul className="max-h-[55vh] space-y-1 overflow-y-auto font-mono text-[11px] text-ink-muted">
            {files.length === 0 && <li>Nenhum arquivo indexado.</li>}
            {files.slice(0, 400).map((file) => (
              <li key={file.path}>
                <Link
                  to={`/projects/${projectId}/editor?path=${encodeURIComponent(file.path)}`}
                  className="block truncate hover:text-brand"
                  title={file.path}
                >
                  {file.path}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "logs",
      label: "Logs",
      content: (
        <EmptyState
          title="Logs de execução"
          description="Aparecem quando o ambiente isolado de execução estiver ativo."
        />
      ),
    },
    {
      id: "database",
      label: "Banco de dados",
      content: (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          <Link
            to={`/projects/${projectId}/database`}
            className="inline-block text-xs text-brand hover:underline"
          >
            Abrir tela completa do banco
          </Link>
          <DatabasePanel projectId={projectId} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <AdvancedModeToggle />
      <Tabs items={advanced ? [...simpleTabs, ...advancedTabs] : simpleTabs} />
    </div>
  );
}
